import { COMMON_PLOT_CONFIG, THEME } from "../../../../constants";
import { useMemo, useState } from "react";

import ColorHash from "color-hash";
import Plot from "react-plotly.js";
import PropTypes from "prop-types";
import { selectTraitsIndexedOnTechnicalName } from "../../../../selectors/traits";
import { slideExportConfig } from "../../../components/charts/slideExport";
import { useSelector } from "react-redux";

const PLOT_STYLE = { width: "100%", height: "100%" };

// Also the title of its card on the PCA page.
export const CIRCLE_CHART_TITLE = "Variables Correlation";

const config = {
  ...COMMON_PLOT_CONFIG,
  ...slideExportConfig({
    title: CIRCLE_CHART_TITLE,
    filename: "PCA Circle chart",
  }),
};

export const CircleChart = ({ computedPCA, selectedComponents }) => {
  const traitsObject = useSelector(selectTraitsIndexedOnTechnicalName);

  // Hidden trace keys (toggled from the accordion) and expanded trait groups.
  const [hidden, setHidden] = useState(() => new Set());
  const [openTraits, setOpenTraits] = useState(() => new Set());

  // One entry per column: its trait, date, polar coordinates, color and a
  // stable key used both to render a trace and to toggle its visibility.
  const items = useMemo(() => {
    const colorHash = new ColorHash({
      hue: { min: 0, max: 360 },
      saturation: 0.8,
      lightness: 0.45,
    });

    return computedPCA.columns.map((column, index) => {
      const x = computedPCA.loadings[selectedComponents[1]][index];
      const y = computedPCA.loadings[selectedComponents[0]][index];
      const r = Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2));
      const thetaDegrees = Math.atan2(y, x) * (180 / Math.PI);

      const splitted = column[0].split("|");
      const prefix = splitted.shift();
      const classes = splitted.map((label) => label.replaceAll("_", " "));
      const trait = traitsObject[prefix];
      const name = `${trait?.name ?? prefix} ${classes.join(" ")}`.trim();
      const date = column[1] ?? "";

      return {
        index,
        trait: name,
        date,
        color: colorHash.hex(name),
        r,
        thetaDegrees,
        key: `${name}__${date}__${index}`,
      };
    });
  }, [
    computedPCA.columns,
    computedPCA.loadings,
    selectedComponents,
    traitsObject,
  ]);

  // The circle has no X/Y axes to title: the horizontal component is written
  // at 0 degrees (right), the vertical one at 90 (top), as angular ticks, by
  // their names ("Comp. 1 (42.1%)", as on the projection axes). The two axes
  // are the angular grid, a cross. Plotly reserves no room for those labels:
  // the right and top margins hold them.
  const layout = useMemo(
    () => ({
      autosize: true,
      responsive: true,
      showlegend: false,
      margin: {
        r: 110,
        t: 30,
        l: 20,
        b: 30,
      },
      plot_bgcolor: "transparent",
      paper_bgcolor: "white",
      polar: {
        radialaxis: {
          range: [0, 1],
          tickvals: [0.25, 0.5, 0.75],
          tickfont: { size: 11.5 },
          showline: true,
        },

        angularaxis: {
          tickmode: "array",
          tickvals: [0, 90, 180, 270],
          ticktext: [
            computedPCA.components[selectedComponents[1]].name,
            computedPCA.components[selectedComponents[0]].name,
            "",
            "",
          ],
          tickfont: { size: 11.5 },
        },
      },

      font: { color: THEME.indicators, size: 13 },
    }),
    [computedPCA.components, selectedComponents]
  );

  const displayData = useMemo(
    () =>
      items.map((item) => ({
        type: "scatterpolar",
        mode: "lines+markers",
        hovertemplate: `<b>${item.trait}</b> <i>${item.date}</i><extra></extra>`,
        marker: { color: item.color },
        r: [0, item.r],
        theta: [0, item.thetaDegrees],
        name: item.date,
        visible: !hidden.has(item.key),
      })),
    [items, hidden]
  );

  // Group columns by trait for the accordion legend.
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      if (!map.has(item.trait))
        map.set(item.trait, {
          trait: item.trait,
          color: item.color,
          dates: [],
        });
      map.get(item.trait).dates.push({ date: item.date, key: item.key });
    });
    return [...map.values()];
  }, [items]);

  const toggleHidden = (key) =>
    setHidden((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleTrait = (trait) =>
    setOpenTraits((prev) => {
      const next = new Set(prev);
      next.has(trait) ? next.delete(trait) : next.add(trait);
      return next;
    });

  return (
    <div className="pca-circle">
      <Plot
        useResizeHandler
        data={displayData}
        layout={layout}
        config={config}
        style={PLOT_STYLE}
      />
      <div className="pca-circle-legend">
        {groups.map((group) => {
          const open = openTraits.has(group.trait);
          const allHidden = group.dates.every(({ key }) => hidden.has(key));
          return (
            <div className="pca-legend-item" key={group.trait}>
              <button
                type="button"
                className="pca-legend-head"
                onClick={() => toggleTrait(group.trait)}
              >
                <span
                  className="pca-legend-swatch"
                  style={{ background: group.color }}
                />
                <span
                  className={`pca-legend-name ${allHidden ? "is-hidden" : ""}`}
                  style={{ color: group.color }}
                >
                  {group.trait}
                </span>
                <span className="pca-legend-count">{group.dates.length}</span>
                <i
                  className={`fa-solid ${
                    open ? "fa-chevron-up" : "fa-chevron-down"
                  }`}
                />
              </button>
              {open && (
                <div className="pca-legend-dates">
                  {group.dates.map(({ date, key }) => (
                    <button
                      type="button"
                      key={key}
                      className={`pca-legend-date ${
                        hidden.has(key) ? "is-hidden" : ""
                      }`}
                      onClick={() => toggleHidden(key)}
                      title="Click to show / hide on the chart"
                    >
                      <span
                        className="pca-legend-dot"
                        style={{ background: group.color }}
                      />
                      {date || "—"}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

CircleChart.propTypes = {
  computedPCA: PropTypes.shape({
    components: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        variance: PropTypes.number.isRequired,
      })
    ).isRequired,
    loadings: PropTypes.array.isRequired,
  }),
  selectedComponents: PropTypes.arrayOf(PropTypes.number).isRequired,
};
